import React from 'react'
import objectHash from 'object-hash'
import { Form, Formik } from 'formik'
import { Grid } from '@mui/material'
import TextField from '../formik/TextField'
import * as Yup from 'yup'
import { useIntl } from 'react-intl'
import messages from '../../shared/messages'
import { MikeButton } from '@mike/mike-shared-frontend'
import { css } from '@emotion/css'
import merge from 'lodash/merge'
import IAPIKey from '../../model/IAPIKey'
import moment from 'moment'

const submitButtonStyle = css`
  float: right;
`

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  expirationDate: Yup.string()
    .required('Expiration date is required')
    .test('min-date', 'Expiration date cannot be in the past', value =>
      Boolean(value && moment.utc(value).isAfter(moment.utc()))
    )
    .test(
      'max-date',
      'Expiration date cannot be more than 1 year from now',
      value =>
        Boolean(
          value && moment.utc(value).isBefore(moment.utc().add(1, 'year'))
        )
    )
})

interface IProps {
  initialValues: IAPIKey
  handleSubmit: (values: IAPIKey) => void
}

const APIKeyForm = (props: IProps) => {
  const intl = useIntl()
  const { handleSubmit, initialValues } = props
  const editing = initialValues.accountId !== undefined

  const defaultInitialValues: IAPIKey = {
    name: '',
    expirationDate: moment()
      .add(1, 'year')
      .toJSON()
  }

  const mergedInitialValues = editing
    ? {
        ...merge({}, defaultInitialValues, initialValues)
      }
    : {
        ...defaultInitialValues
      }

  const convertToRepresentation = (dateString: string | undefined) =>
    moment.utc(dateString).format('YYYY-MM-DDTHH:mm')

  const convertToModel = (dateString: string) =>
    moment.utc(dateString).format('YYYY-MM-DDTHH:mm')

  const minDateTime = moment.utc().format('YYYY-MM-DDTHH:mm')

  const maxDateTime = moment
    .utc()
    .add(1, 'year')
    .format('YYYY-MM-DDTHH:mm')

  return (
    <Formik
      key={objectHash(mergedInitialValues)}
      initialValues={mergedInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {formik => {
        const { handleChange, setFieldTouched, setFieldValue } = formik
        const change = (name, e) => {
          handleChange(e)
          setFieldTouched(name, true, false)
        }

        const changeDate = (name, e) => {
          if (!editing) {
            setFieldValue(
              'expirationDate',
              convertToModel(e.target.value),
              true
            )
            setFieldTouched(name, true, false)
          }
        }

        return (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label={intl.formatMessage(messages.name)}
                  fullWidth
                  required
                  autoFocus
                  onChange={change.bind(null, 'name')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="expirationDate"
                  label={intl.formatMessage({
                    id: 'screens.apiAccess.expirationDate'
                  })}
                  fullWidth
                  required
                  type="datetime-local"
                  disabled={editing}
                  value={convertToRepresentation(formik.values.expirationDate)}
                  onChange={changeDate.bind(null, 'expirationDate')}
                  InputLabelProps={{
                    shrink: true
                  }}
                  InputProps={{
                    inputProps: {
                      min: minDateTime,
                      max: maxDateTime
                    }
                  }}
                />
              </Grid>
              {editing && (
                <Grid item xs={12}>
                  <TextField
                    name="key"
                    label={intl.formatMessage({
                      id: 'screens.apiAccess.apiKey'
                    })}
                    fullWidth
                    disabled
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <MikeButton
                  className={submitButtonStyle}
                  color="secondary"
                  variant="contained"
                  type="submit"
                  disabled={!formik.isValid || formik.isSubmitting}
                >
                  {editing
                    ? intl.formatMessage({
                        id: 'apiKeyForm.updateAPIKeyButton'
                      })
                    : intl.formatMessage({
                        id: 'apiKeyForm.createAPIKeyButton'
                      })}
                </MikeButton>
              </Grid>
            </Grid>
          </Form>
        )
      }}
    </Formik>
  )
}

export default APIKeyForm
